import {ref, reactive, computed, watch} from 'vue';
import cssvar from '@/scss/_variables.scss';
import {isTouchDevice} from "@/modules/utils/deviceUtils";
import config from "@/config/config.json";

const pageResizeState = reactive({
    baseWidth:0,
    baseHeight:0,
    imgToBrowserScale:1, //imgToBrowserScale is the ratio between the page image height and the browser inner window height. It'll be used to position annotations and scale all other elements
    currentInitialRatio:1, //get the imgToBrowserScale when the app has just finished loading; used for positioning note on window resizing
    zoomLevel:1, //for single page
    zoomLevelLeft:1, //left page zoom level in double page mode
    zoomLevelRight:1, //right  page zoom level in double page mode
    isBaseWidthHeightReady:computed(()=>{
        return (pageResizeState.baseWidth && pageResizeState.baseHeight) ? true : false
    }),
    fittedBrowserWidth:computed(()=>{
        //for easier access to the page content width that has been scaled to fit the browser
        return Math.round(pageResizeState.baseWidth * pageResizeState.imgToBrowserScale);
    }),
    fittedBrowserHeight:computed(()=>{
        //for easier access to the page content height that has been scaled to fit the browser
        return Math.round(pageResizeState.baseHeight * pageResizeState.imgToBrowserScale);
    }),
    fittedBrowserZoomedWidth:computed(()=>{
        return Math.round(pageResizeState.fittedBrowserWidth * pageResizeState.zoomLevel);
    }),
    fittedBrowserZoomedHeight:computed(()=>{
        return Math.round(pageResizeState.fittedBrowserHeight * pageResizeState.zoomLevel);
    }),
    fittedBrowserZoomedLeftWidth:computed(()=>{
        return Math.round(pageResizeState.fittedBrowserWidth * pageResizeState.zoomLevelLeft);
    }),
    fittedBrowserZoomedLeftHeight:computed(()=>{
        return Math.round(pageResizeState.fittedBrowserHeight * pageResizeState.zoomLevelLeft);
    }),
    fittedBrowserZoomedRightWidth:computed(()=>{
        return Math.round(pageResizeState.fittedBrowserWidth * pageResizeState.zoomLevelRight);
    }),
    fittedBrowserZoomedRightHeight:computed(()=>{
        return Math.round(pageResizeState.fittedBrowserHeight * pageResizeState.zoomLevelRight);
    }),
})

//const { pageState } = usePaging();

export function usePageResize() {

    /**
     * Get the original pdf width, it's used to locate the annotation and drawing coordinate accurately.
     * Please refer to the pdf image convert tool, in which the pdf viewport is set to scale by 4 times to get the optimized resolution.
     * The scale factor 4 is set to imageDensity.
     */
    const setBaseWidth = (width ) => {
        pageResizeState.baseWidth =  width / config.reader.imageDensity;
    }

    /**
     * Get the original pdf height, it's used to locate the annotation and drawing coordinate accurately.
     * Please refer to the pdf image convert tool, in which the pdf viewport is set to scale by 4 times to get the optimized resolution.
     * The scale factor 4 is set to imageDensity.
     */
    const setBaseHeight = (width ) => {
        pageResizeState.baseHeight =  width / config.reader.imageDensity;
    }

    /**
     * Tell how much the page image is scaled to fit into browser.
     * Since tha annotation position and size are relative to the image physical size,
     * we'll update the imgToBrowserScale value when window size is changed,
     * in order to locate the annotation and drawing coordinates accurately.
     * @param height
     */
    const setFittedBrowserHeight = (height, width) => {

        let browserHeightToWidthRatio = height/ width;
        let imgBaseHeightToWidthRatio = pageResizeState.baseHeight / pageResizeState.baseWidth;

        if (browserHeightToWidthRatio < imgBaseHeightToWidthRatio) {
            pageResizeState.imgToBrowserScale = Math.round(height / pageResizeState.baseHeight * 1000) / 1000;
        } else {
            pageResizeState.imgToBrowserScale = Math.round(width / pageResizeState.baseWidth * 1000) / 1000;
        }

        //pageResizeState.imgToBrowserScale = Math.round(height / pageResizeState.baseHeight * 1000) / 1000;
        //pageResizeState.imgToBrowserScale = Math.round(height / pageResizeState.baseHeight * 1000) / 1000;

        console.log(" pageResizeState.imgToBrowserScale : " +  pageResizeState.imgToBrowserScale )
    }

    /**
     * Zoom In in single page mode
     */
    const zoomIn = (pinchScale) => {

        console.log("@zoomin 1")
        if (pageResizeState.zoomLevel < config.reader.maxZoom ) {
            console.log("@zoomin 2")
            /*
            if (isTouchDevice()) {
                pageResizeState.zoomLevel += pinchScale;
            } else {
                pageResizeState.zoomLevel += config.reader.zoomInterval;
            }*/
            pageResizeState.zoomLevel += config.reader.zoomInterval;

        }  else {
            pageResizeState.zoomLevel = config.reader.maxZoom;
        }
    }

    /**
     * Zoom Out in single page mode
     */
    const zoomOut = (pinchScale) => {

        console.log("@zoomout 1")
        if (pageResizeState.zoomLevel > config.reader.minZoom) {
            console.log("@zoomout 2")
            /*
            if (isTouchDevice()) {
                pageResizeState.zoomLevel -= pinchScale;
            } else {
                pageResizeState.zoomLevel -= config.reader.zoomInterval;
            }*/
            pageResizeState.zoomLevel -= config.reader.zoomInterval;
        } else {
            pageResizeState.zoomLevel = config.reader.minZoom;
        }
    }

    /**
     * Zoom In left page in double page mode
     */
    const zoomInLeftPage = (pinchScale) => {

        if (pageResizeState.zoomLevelLeft < config.reader.maxZoom) {
            /*
            if (isTouchDevice()) {
                pageResizeState.zoomLevelLeft += pinchScale;
            } else {
                pageResizeState.zoomLevelLeft += config.reader.zoomInterval;
            }*/
            pageResizeState.zoomLevelLeft += config.reader.zoomInterval;
        } else {
            pageResizeState.zoomLevelLeft = config.reader.maxZoom;
        }
    }

    /**
     * Zoom Out left page in double page mode
     */
    const zoomOutLeftPage = (pinchScale) => {

        if (pageResizeState.zoomLevelLeft > config.reader.minZoom) {
            /*
            if (isTouchDevice()) {
                pageResizeState.zoomLevelLeft -= pinchScale;
            } else {
                pageResizeState.zoomLevelLeft -= config.reader.zoomInterval;
            }*/
            pageResizeState.zoomLevelLeft -= config.reader.zoomInterval;
        } else {
            pageResizeState.zoomLevelLeft = config.reader.minZoom
        }
    }

    /**
     * Zoom In right page in double page mode
     */
    const zoomInRightPage = (pinchScale) => {

        if (pageResizeState.zoomLevelRight < config.reader.maxZoom) {
            /*
            if (isTouchDevice()) {
                pageResizeState.zoomLevelRight += pinchScale;
            } else {
                pageResizeState.zoomLevelRight += config.reader.zoomInterval;
            }*/
            pageResizeState.zoomLevelRight += config.reader.zoomInterval;
        } else {
            pageResizeState.zoomLevelRight = config.reader.maxZoom
        }
    }

    /**
     * Zoom Out right page in double page mode
     */
    const zoomOutRightPage = (pinchScale) => {

        if (pageResizeState.zoomLevelRight > config.reader.minZoom) {
            /*
            if (isTouchDevice()) {
                pageResizeState.zoomLevelRight -= pinchScale;
            } else {
                pageResizeState.zoomLevelRight -= config.reader.zoomInterval;
            }*/
            pageResizeState.zoomLevelRight -= config.reader.zoomInterval;
        } else {
            pageResizeState.zoomLevelRight = config.reader.minZoom
        }

    }

    const reset =() => {
        console.log("reset @pageResize.js")
        pageResizeState.currentInitialRatio = 1;
        pageResizeState.baseWidth=0;
        pageResizeState.baseHeight=0;
        pageResizeState.imgToBrowserScale=1;
        pageResizeState.zoomLevel=1;
        pageResizeState.zoomLevelLeft=1;
        pageResizeState.zoomLevelRight=1;
    }

    return {
        pageResizeState,
        setFittedBrowserHeight,
        setBaseWidth,
        setBaseHeight,
        zoomIn,
        zoomOut,
        zoomInLeftPage,
        zoomInRightPage,
        zoomOutLeftPage,
        zoomOutRightPage,
        reset
    }


}
